import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { isAdmin } from '../../utils/permissions';
import { ROLE_KEYS } from '../../constants/roles';
import useTranslation from '../../hooks/useTranslation';
import PortalDropdown from '../../components/ui/PortalDropdown';
import UserAvatar from '../../components/ui/UserAvatar';
import Icon from '../../components/AppIcon';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import {
  listUsers,
  updateUserRole,
  setOnsitePlusEnabled,
  setUserActive,
  setCanViewAllProjects,
  setUserTrade,
  inviteUser,
} from '../../services/userManagementService';
// Les metiers viennent de organization_trades — une liste que chaque organisation
// configure elle-meme. Les valeurs ne se traduisent pas : ce sont des donnees.
import organizationTradesService from '../../services/organizationTradesService';
import { getCurrentOrgId } from '../../lib/getCurrentOrgId';
import organizationService from '../../services/organizationService';
import { supabase } from '../../lib/supabase';
import { statusVariant } from '../../utils/statusVariant';
import Badge from '../../components/ui/Badge';

// Ordre partage avec la liste des gabarits (constants/roles.js).
const ROLES = ROLE_KEYS;

// ─── Invite User Modal ─────────────────────────────────────────────────────────
const INVITE_ROLES = ROLE_KEYS;

const generatePassword = () => {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const symbols = '!@#$%^&*()-_=+';
  const all = upper + lower + digits + symbols;
  const arr = new Uint8Array(16);
  crypto.getRandomValues(arr);
  let result = '';
  // Guarantee at least one symbol
  result += symbols?.[arr?.[0] % symbols?.length];
  result += upper?.[arr?.[1] % upper?.length];
  result += digits?.[arr?.[2] % digits?.length];
  for (let i = 3; i < 16; i++) {
    result += all?.[arr?.[i] % all?.length];
  }
  // Shuffle
  return result?.split('')?.sort(() => Math.random() - 0.5)?.join('');
};

const InviteUserModal = ({ onClose, onSuccess, trades, t }) => {
  const inputClass = 'w-full border border-border rounded-lg px-3 py-2 text-sm text-[#2C3E50] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary';

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState('pm');
  const [roleOpen, setRoleOpen] = useState(false);
  const roleRef = useRef(null);
  // Metier — facultatif. L'Edge Function d'invitation ne le connait pas : il est
  // ecrit juste apres, sur le profil que l'invitation vient de creer.
  const [tradeId, setTradeId] = useState('');
  const [tradeOpen, setTradeOpen] = useState(false);
  const tradeRef = useRef(null);
  const [onsitePlus, setOnsitePlus] = useState(false);
  const [mode, setMode] = useState('password');
  const [tempPassword, setTempPassword] = useState('');
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const handleGenerate = () => {
    setTempPassword(generatePassword());
    setCopied(false);
  };

  const handleCopy = async () => {
    if (!tempPassword) return;
    try {
      await navigator.clipboard?.writeText(tempPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      // clipboard not available
    }
  };

  const handleSubmit = async () => {
    setFormError(null);

    const trimmedEmail = email?.trim()?.toLowerCase();
    if (!trimmedEmail) {
      setFormError(t('userManagement.errors.inviteEmailRequired'));
      return;
    }

    if (mode === 'password' && tempPassword?.length < 12) {
      setFormError(t('userManagement.errors.invitePasswordTooShort'));
      return;
    }

    const payload = {
      email: trimmedEmail,
      full_name: fullName?.trim() || undefined,
      role,
      language: 'fr',
      product_access: {
        onsitepm: true,
        onsiteplus: onsitePlus,
        onsitees: false,
      },
      mode,
      ...(mode === 'password' ? { temp_password: tempPassword } : {}),
    };

    setSubmitting(true);
    let result = await inviteUser(payload);
    setSubmitting(false);

    if (result?.error) {
      const status = result?.status;
      let msg;
      if (status === 402) msg = t('userManagement.errors.inviteSeatLimit');
      else if (status === 409) msg = t('userManagement.errors.inviteEmailExists');
      else if (status === 403) msg = t('userManagement.errors.inviteNotAdmin');
      else if (status === 400) msg = result?.error;
      else msg = t('userManagement.errors.inviteFailed');
      setFormError(msg);
      return;
    }

    // Le profil existe maintenant : on y pose le metier. Un echec ici ne doit pas
    // faire echouer la creation — l'utilisateur est cree, le metier se corrige
    // dans la liste.
    const newUserId = result?.data?.user_id;
    if (newUserId && tradeId) {
      const { error: tradeError } = await setUserTrade(newUserId, tradeId);
      if (tradeError) console.error('[userManagement] setUserTrade after invite:', tradeError);
    }

    onSuccess(mode);
  };

  const currentTrade = trades?.find((tr) => tr?.id === tradeId) || null;

  const tradeItems = [
    {
      label: t('userManagement.invite.tradePlaceholder'),
      onClick: () => { setTradeId(''); setTradeOpen(false); },
      active: !tradeId,
    },
    ...(trades || [])?.map((tr) => ({
      label: tr?.name,
      onClick: () => { setTradeId(tr?.id); setTradeOpen(false); },
      active: tr?.id === tradeId,
    })),
  ];

  const roleItems = INVITE_ROLES?.map((r) => ({
    label: t(`userManagement.roles.${r}`) || r,
    onClick: () => { setRole(r); setRoleOpen(false); },
    active: r === role,
  }));

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="mb-5">
          <h3 className="text-base font-semibold text-[#2C3E50]">{t('userManagement.invite.title')}</h3>
          <p className="text-sm text-[#6B7280] mt-0.5">{t('userManagement.invite.subtitle')}</p>
        </div>

        <div className="space-y-4">
          {/* Email */}
          <div>
            <label className="block text-xs font-medium text-[#2C3E50] mb-1">
              {t('userManagement.invite.emailLabel')} <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e?.target?.value)}
              placeholder={t('userManagement.invite.emailPlaceholder')}
              className={inputClass}
              disabled={submitting}
            />
          </div>

          {/* Full Name */}
          <div>
            <label className="block text-xs font-medium text-[#2C3E50] mb-1">
              {t('userManagement.invite.fullNameLabel')}
            </label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e?.target?.value)}
              placeholder={t('userManagement.invite.fullNamePlaceholder')}
              className={inputClass}
              disabled={submitting}
            />
          </div>

          {/* Role */}
          <div>
            <label className="block text-xs font-medium text-[#2C3E50] mb-1">
              {t('userManagement.invite.roleLabel')}
            </label>
            <div className="relative">
              <button
                ref={roleRef}
                type="button"
                onClick={() => setRoleOpen((v) => !v)}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border bg-white text-sm font-medium text-[#2C3E50] hover:border-primary hover:bg-orange-50 transition-colors w-full justify-between disabled:opacity-60"
              >
                {t(`userManagement.roles.${role}`) || role}
                <Icon name="ChevronDown" size={14} className="text-[#6B7280]" />
              </button>
              <PortalDropdown anchorRef={roleRef} open={roleOpen} onClose={() => setRoleOpen(false)} width={220} items={roleItems} />
            </div>
          </div>

          {/* Metier — facultatif. Les libelles viennent de la base, jamais traduits. */}
          <div>
            <label className="block text-xs font-medium text-[#2C3E50] mb-1">
              {t('userManagement.invite.tradeLabel')}
            </label>
            <div className="relative">
              <button
                ref={tradeRef}
                type="button"
                onClick={() => setTradeOpen((v) => !v)}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border bg-white text-sm font-medium text-[#2C3E50] hover:border-primary hover:bg-orange-50 transition-colors w-full justify-between disabled:opacity-60"
              >
                <span className={currentTrade ? '' : 'text-[#9CA3AF]'}>
                  {currentTrade?.name || t('userManagement.invite.tradePlaceholder')}
                </span>
                <Icon name="ChevronDown" size={14} className="text-[#6B7280]" />
              </button>
              <PortalDropdown anchorRef={tradeRef} open={tradeOpen} onClose={() => setTradeOpen(false)} width={220} items={tradeItems} />
            </div>
          </div>

          {/* Product Access */}
          <div>
            <label className="block text-xs font-medium text-[#2C3E50] mb-2">
              {t('userManagement.invite.productAccessLabel')}
            </label>
            <div className="space-y-2">
              {/* OnsitePM — always checked, disabled */}
              <label className="flex items-center gap-2 text-sm text-[#2C3E50] cursor-not-allowed opacity-70">
                <input type="checkbox" checked readOnly disabled className="rounded border-border accent-primary" />
                {t('userManagement.invite.productOnsitePM')}
              </label>
              {/* OnSite+ — optional */}
              <label className="flex items-center gap-2 text-sm text-[#2C3E50] cursor-pointer">
                <input
                  type="checkbox"
                  checked={onsitePlus}
                  onChange={(e) => setOnsitePlus(e?.target?.checked)}
                  disabled={submitting}
                  className="rounded border-border accent-primary"
                />
                {t('userManagement.invite.productOnsitePlus')}
              </label>
              {/* OnsiteES — disabled, coming soon */}
              <label className="flex items-center gap-2 text-sm text-[#9CA3AF] cursor-not-allowed">
                <input type="checkbox" readOnly disabled className="rounded border-border" />
                {t('userManagement.invite.productOnsiteES')}
                <span className="text-xs text-[#9CA3AF] italic">({t('userManagement.invite.productComingSoon')})</span>
              </label>
            </div>
          </div>

          {/* Activation Mode */}
          <div>
            <label className="block text-xs font-medium text-[#2C3E50] mb-2">
              {t('userManagement.invite.modeLabel')}
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-[#2C3E50] cursor-pointer">
                <input
                  type="radio"
                  name="invite-mode"
                  value="password"
                  checked={mode === 'password'}
                  onChange={() => setMode('password')}
                  disabled={submitting}
                  className="accent-primary"
                />
                {t('userManagement.invite.modePassword')}
              </label>
              <label className="flex items-center gap-2 text-sm text-[#2C3E50] cursor-pointer">
                <input
                  type="radio"
                  name="invite-mode"
                  value="invite"
                  checked={mode === 'invite'}
                  onChange={() => setMode('invite')}
                  disabled={submitting}
                  className="accent-primary"
                />
                {t('userManagement.invite.modeEmail')}
              </label>
            </div>
          </div>

          {/* Temporary Password (mode=password only) */}
          {mode === 'password' && (
            <div>
              <label className="block text-xs font-medium text-[#2C3E50] mb-1">
                {t('userManagement.invite.tempPasswordLabel')}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tempPassword}
                  onChange={(e) => setTempPassword(e?.target?.value)}
                  className={`${inputClass} flex-1`}
                  disabled={submitting}
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={submitting}
                  className="px-3 py-2 text-xs rounded-lg border border-border text-[#2C3E50] hover:bg-muted transition-colors whitespace-nowrap disabled:opacity-60"
                >
                  {t('userManagement.invite.generate')}
                </button>
                <button
                  type="button"
                  onClick={handleCopy}
                  disabled={submitting || !tempPassword}
                  className="px-3 py-2 text-xs rounded-lg border border-border text-[#2C3E50] hover:bg-muted transition-colors whitespace-nowrap disabled:opacity-60"
                >
                  {copied ? t('userManagement.invite.copied') : t('userManagement.invite.copy')}
                </button>
              </div>
            </div>
          )}

          {/* Inline error */}
          {formError && (
            <p className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{formError}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm rounded-lg border border-border text-[#6B7280] hover:bg-muted transition-colors disabled:opacity-60"
          >
            {t('userManagement.invite.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-dark transition-colors disabled:opacity-60"
          >
            {submitting ? (
              <>
                <Icon name="Loader2" size={14} className="animate-spin" />
                {t('userManagement.invite.submitting')}
              </>
            ) : (
              t('userManagement.invite.submit')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Role Dropdown per row ─────────────────────────────────────────────────────
const RoleDropdown = ({ user, currentUserId, onRoleChange, t }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const isSelf = user?.id === currentUserId;

  if (isSelf) {
    return (
      <div className="relative group inline-block">
        <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border bg-muted text-xs font-medium text-[#6B7280] cursor-not-allowed select-none">
          {t(`userManagement.roles.${user?.role}`) || user?.role}
          <Icon name="Lock" size={12} className="text-[#9CA3AF]" />
        </span>
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50 pointer-events-none">
          <div className="bg-[#2C3E50] text-white text-xs rounded-md px-2 py-1 whitespace-nowrap shadow-lg">
            {t('userManagement.tooltips.cannotEditOwnRole')}
          </div>
        </div>
      </div>
    );
  }

  const roleItems = ROLES?.map((role) => ({
    label: t(`userManagement.roles.${role}`) || role,
    onClick: () => {
      setOpen(false);
      if (role !== user?.role) onRoleChange(user, role);
    },
    active: role === user?.role,
  }));

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-white text-xs font-medium text-[#2C3E50] hover:border-primary hover:bg-orange-50 transition-colors"
      >
        {t(`userManagement.roles.${user?.role}`) || user?.role}
        <Icon name="ChevronDown" size={12} className="text-[#6B7280]" />
      </button>
      <PortalDropdown anchorRef={btnRef} open={open} onClose={() => setOpen(false)} width={160} items={roleItems} />
    </>
  );
};

// ─── Trade Dropdown per row ────────────────────────────────────────────────────
/**
 * Le metier d'une personne, choisi dans organization_trades.
 *
 * FACULTATIF : un charge de projet n'en a pas. L'option vide est en tete et
 * efface le champ. Les libelles viennent de la base et ne sont JAMAIS traduits —
 * ce sont des donnees de l'organisation, que Mazine enrichit lui-meme.
 */
const TradeDropdown = ({ user, trades, onTradeChange, t }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);

  const current = trades?.find((tr) => tr?.id === user?.trade_id) || null;

  const items = [
    {
      label: t('userManagement.invite.tradePlaceholder'),
      onClick: () => { setOpen(false); if (user?.trade_id) onTradeChange(user, null); },
      active: !user?.trade_id,
    },
    ...(trades || [])?.map((tr) => ({
      label: tr?.name,
      onClick: () => { setOpen(false); if (tr?.id !== user?.trade_id) onTradeChange(user, tr?.id); },
      active: tr?.id === user?.trade_id,
    })),
  ];

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-white text-xs font-medium text-[#2C3E50] hover:border-primary hover:bg-orange-50 transition-colors max-w-full"
      >
        <span className="truncate">
          {current?.name || <span className="text-[#9CA3AF]">—</span>}
        </span>
        <Icon name="ChevronDown" size={12} className="text-[#6B7280] flex-shrink-0" />
      </button>
      <PortalDropdown anchorRef={btnRef} open={open} onClose={() => setOpen(false)} width={200} items={items} />
    </>
  );
};

// ─── Actions Overflow Menu ─────────────────────────────────────────────────────
const ActionsMenu = ({ user, currentUserId, onToggleActive, t }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const isSelf = user?.id === currentUserId;

  const items = [
    {
      label: user?.is_active
        ? t('userManagement.actions.deactivate')
        : t('userManagement.actions.reactivate'),
      icon: user?.is_active ? 'UserX' : 'UserCheck',
      onClick: () => { setOpen(false); onToggleActive(user); },
      variant: user?.is_active ? 'danger' : 'default',
    },
  ];

  return (
    <>
      <div className="relative group inline-block">
        <button
          ref={btnRef}
          onClick={() => !isSelf && setOpen((v) => !v)}
          disabled={isSelf}
          className={`bg-transparent outline-none focus:outline-none focus:ring-2 focus:ring-primary/20 p-1 rounded-full transition-colors ${
            isSelf
              ? 'text-[#D1D5DB] cursor-not-allowed'
              : 'text-[#6B7280] hover:bg-gray-50 hover:text-[#2C3E50]'
          }`}
        >
          <Icon name="MoreVertical" size={16} />
        </button>
        {isSelf && (
          <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block z-50 pointer-events-none">
            <div className="bg-[#2C3E50] text-white text-xs rounded-md px-2 py-1 whitespace-nowrap shadow-lg">
              {t('userManagement.tooltips.cannotDeactivateSelf')}
            </div>
          </div>
        )}
      </div>
      <PortalDropdown
        anchorRef={btnRef}
        open={open}
        onClose={() => setOpen(false)}
        width={220}
        items={items}
      />
    </>
  );
};

// ─── Org Info Section (admin-only) ────────────────────────────────────────────
const OrgInfoSection = ({ t }) => {
  const [orgId, setOrgId] = useState(null);
  const [orgName, setOrgName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [tagline, setTagline] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [rbqNumber, setRbqNumber] = useState('');
  const [tpsNumber, setTpsNumber] = useState('');
  const [tvqNumber, setTvqNumber] = useState('');
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      const id = await getCurrentOrgId();
      if (!id) return;
      setOrgId(id);
      const { data } = await organizationService?.getOrgInfo(id);
      if (data) {
        setOrgName(data?.name || '');
        setAddress(data?.address || '');
        setPhone(data?.phone || '');
        setTagline(data?.tagline || '');
        setEmail(data?.email || '');
        setWebsite(data?.website || '');
        setRbqNumber(data?.rbq_number || '');
        setTpsNumber(data?.tps_number || '');
        setTvqNumber(data?.tvq_number || '');
        // Load logo preview from public URL if path exists
        if (data?.logo_url) {
          const { data: urlData } = supabase?.storage
            ?.from('organization-logos')
            ?.getPublicUrl(data?.logo_url);
          if (urlData?.publicUrl) {
            setLogoPreview(`${urlData?.publicUrl}?v=${Date.now()}`);
          }
        }
      }
    };
    load();
  }, []);

  const showToastMsg = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const handleLogoChange = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !orgId) return;

    // Validate SVG
    if (file?.type === 'image/svg+xml') {
      showToastMsg('error', t('settings.organization.logoSvgNotSupported'));
      return;
    }
    // Validate size
    if (file?.size > 5242880) {
      showToastMsg('error', t('settings.organization.logoTooLarge'));
      return;
    }

    setLogoUploading(true);
    try {
      const publicUrl = await organizationService?.uploadLogo(orgId, file);
      setLogoPreview(publicUrl);
      showToastMsg('success', t('settings.organization.logoUploadSuccess'));
    } catch (err) {
      if (err?.message === 'SVG_NOT_SUPPORTED') {
        showToastMsg('error', t('settings.organization.logoSvgNotSupported'));
      } else if (err?.message === 'FILE_TOO_LARGE') {
        showToastMsg('error', t('settings.organization.logoTooLarge'));
      } else {
        showToastMsg('error', t('userManagement.errors.updateFailed'));
      }
    } finally {
      setLogoUploading(false);
      if (fileInputRef?.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    if (!orgId) return;
    setLogoUploading(true);
    try {
      await organizationService?.removeLogo(orgId);
      setLogoPreview(null);
      showToastMsg('success', t('settings.organization.logoRemoveSuccess'));
    } catch {
      showToastMsg('error', t('userManagement.errors.updateFailed'));
    } finally {
      setLogoUploading(false);
    }
  };

  const handleSave = async () => {
    if (!orgId) return;
    setSaving(true);
    try {
      await organizationService?.updateOrgInfo(orgId, {
        address,
        phone,
        tagline,
        email,
        website,
        rbq_number: rbqNumber,
        tps_number: tpsNumber,
        tvq_number: tvqNumber,
      });
      showToastMsg('success', t('settings.organization.saveSuccess'));
    } catch {
      showToastMsg('error', t('userManagement.errors.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full border border-border rounded-lg px-3 py-2 text-sm text-[#2C3E50] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary";
  const labelClass = "block text-xs font-semibold text-[#374151] mb-1 uppercase tracking-wide";

  return (
    <div className="bg-card border border-border rounded-xl shadow-sm p-6 mt-6">
      <h2 className="text-base font-semibold text-[#2C3E50] mb-1">
        {t('settings.organization.title')}
      </h2>
      <p className="text-xs text-[#6B7280] mb-5">{orgName}</p>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* LEFT COLUMN — Logo upload widget */}
        <div className="flex-shrink-0 w-full lg:w-56">
          <label className={labelClass}>{t('settings.organization.logoLabel')}</label>
          {/* Logo preview */}
          <div className="mt-2 mb-3 w-full h-28 border border-dashed border-border rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="Company logo"
                className="max-w-full max-h-full object-contain"
                style={{ maxWidth: 200, maxHeight: 80 }}
              />
            ) : (
              <div className="flex flex-col items-center gap-1 text-gray-400">
                <Icon name="Image" size={28} />
                <span className="text-xs">{t('settings.organization.logoLabel')}</span>
              </div>
            )}
          </div>
          {/* Upload / Remove buttons */}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => fileInputRef?.current?.click()}
              disabled={logoUploading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-white text-xs font-medium text-[#2C3E50] hover:border-primary hover:bg-orange-50 transition-colors disabled:opacity-60"
            >
              {logoUploading ? (
                <Icon name="Loader2" size={12} className="animate-spin" />
              ) : (
                <Icon name="Upload" size={12} />
              )}
              {t('settings.organization.logoUploadBtn')}
            </button>
            {logoPreview && (
              <button
                onClick={handleRemoveLogo}
                disabled={logoUploading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 bg-white text-xs font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-60"
              >
                <Icon name="Trash2" size={12} />
                {t('settings.organization.logoRemoveBtn')}
              </button>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={handleLogoChange}
          />
          <p className="mt-2 text-[10px] text-[#9CA3AF] leading-tight">
            {t('settings.organization.logoSvgNotSupported')}
          </p>
        </div>

        {/* RIGHT COLUMN — Form fields */}
        <div className="flex-1 min-w-0">
          {/* Row 1: Name (read-only) | Tagline */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className={labelClass}>Nom</label>
              <input
                type="text"
                value={orgName}
                readOnly
                className={`${inputClass} bg-gray-50 cursor-not-allowed text-[#9CA3AF]`}
              />
            </div>
            <div>
              <label className={labelClass}>{t('settings.organization.taglineLabel')}</label>
              <input
                type="text"
                value={tagline}
                onChange={(e) => setTagline(e?.target?.value)}
                className={inputClass}
                placeholder="Building with precision · Bâtir avec précision"
              />
            </div>
          </div>

          {/* Row 2: Address (full width) */}
          <div className="mb-4">
            <label className={labelClass}>{t('settings.organization.addressLabel')}</label>
            <textarea
              value={address}
              onChange={(e) => setAddress(e?.target?.value)}
              rows={2}
              className={`${inputClass} resize-none`}
              placeholder="123 Rue Exemple, Montréal, QC  H1A 1A1"
            />
          </div>

          {/* Row 3: Phone | Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className={labelClass}>{t('settings.organization.phoneLabel')}</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e?.target?.value)}
                className={inputClass}
                placeholder="+1 (514) 000-0000"
              />
            </div>
            <div>
              <label className={labelClass}>{t('settings.organization.emailLabel')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                className={inputClass}
                placeholder="infos@example.com"
              />
            </div>
          </div>

          {/* Row 4: Website | RBQ */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className={labelClass}>{t('settings.organization.websiteLabel')}</label>
              <input
                type="text"
                value={website}
                onChange={(e) => setWebsite(e?.target?.value)}
                className={inputClass}
                placeholder="www.example.com"
              />
            </div>
            <div>
              <label className={labelClass}>{t('settings.organization.rbqLabel')}</label>
              <input
                type="text"
                value={rbqNumber}
                onChange={(e) => setRbqNumber(e?.target?.value)}
                className={inputClass}
                placeholder="5782-3015-01"
              />
            </div>
          </div>

          {/* Row 5: TPS | TVQ */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            <div>
              <label className={labelClass}>{t('settings.organization.tpsLabel')}</label>
              <input
                type="text"
                value={tpsNumber}
                onChange={(e) => setTpsNumber(e?.target?.value)}
                className={inputClass}
                placeholder="709900716"
              />
            </div>
            <div>
              <label className={labelClass}>{t('settings.organization.tvqLabel')}</label>
              <input
                type="text"
                value={tvqNumber}
                onChange={(e) => setTvqNumber(e?.target?.value)}
                className={inputClass}
                placeholder="1226411085"
              />
            </div>
          </div>

          {/* Save button */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-dark transition-colors disabled:opacity-60"
          >
            {saving ? (
              <>
                <Icon name="Loader2" size={14} className="animate-spin" />
                {t('common.saving')}
              </>
            ) : (
              t('settings.organization.saveBtn')
            )}
          </button>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div
          className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
            toast?.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
          }`}
        >
          <Icon name={toast?.type === 'error' ? 'AlertCircle' : 'CheckCircle'} size={14} />
          {toast?.message}
        </div>
      )}
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────
const UserManagementPage = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { t } = useTranslation();

  const [users, setUsers] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pending action state for confirmation modals
  const [pendingAction, setPendingAction] = useState(null);
  // blocked modal (last-admin)
  const [blockedModal, setBlockedModal] = useState(false);
  const [blockedReason, setBlockedReason] = useState('last_admin');
  // toast
  const [toast, setToast] = useState(null);
  // invite modal
  const [showInviteModal, setShowInviteModal] = useState(false);

  // ── Admin guard ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (userProfile && !isAdmin(userProfile)) {
      navigate('/project-detail', { replace: true });
    }
  }, [userProfile, navigate]);

  // ── Fetch users ──────────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const { data, error: fetchError } = await listUsers();
    if (fetchError) {
      setError(t('userManagement.errors.loadFailed'));
    } else {
      setUsers(data);
    }
    setLoading(false);
  }, [t]);

  useEffect(() => {
    if (isAdmin(userProfile)) fetchUsers();
  }, [userProfile, fetchUsers]);

  // Le service filtre deja sur is_active et trie par order_index puis name.
  useEffect(() => {
    if (!isAdmin(userProfile)) return;
    organizationTradesService?.getByOrg()
      ?.then((rows) => setTrades(rows || []))
      ?.catch((err) => console.error('[userManagement] trades:', err));
  }, [userProfile]);

  // ── Toast helper ─────────────────────────────────────────────────────────────
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Role change flow ─────────────────────────────────────────────────────────
  // Le metier n'ouvre aucun acces : il change sans confirmation, contrairement
  // a la fonction. Mise a jour optimiste, annulee si la base refuse.
  const handleTradeChange = async (user, tradeId) => {
    const previous = user?.trade_id ?? null;
    setUsers((prev) => prev?.map((u) => (u?.id === user?.id ? { ...u, trade_id: tradeId } : u)));

    const { error: tradeError } = await setUserTrade(user?.id, tradeId);
    if (tradeError) {
      setUsers((prev) => prev?.map((u) => (u?.id === user?.id ? { ...u, trade_id: previous } : u)));
      showToast(t('userManagement.errors.loadFailed'), 'error');
    }
  };

  const handleRoleChange = (user, newRole) => {
    setPendingAction({
      type: 'role',
      user,
      newRole,
      title: t('userManagement.modals.roleChange.title'),
      message: t('userManagement.modals.roleChange.message')?.replace('{name}', user?.full_name || user?.email)?.replace('{oldRole}', t(`userManagement.roles.${user?.role}`) || user?.role)?.replace('{newRole}', t(`userManagement.roles.${newRole}`) || newRole),
    });
  };

  const confirmRoleChange = async () => {
    const { user, newRole } = pendingAction;
    setPendingAction(null);

    // Optimistic update
    setUsers((prev) =>
      prev?.map((u) => (u?.id === user?.id ? { ...u, role: newRole } : u))
    );

    let result = await updateUserRole(user?.id, newRole, user?.role);

    if (result?.blocked) {
      // Revert
      setUsers((prev) =>
        prev?.map((u) => (u?.id === user?.id ? { ...u, role: user?.role } : u))
      );
      setBlockedReason('last_admin');
      setBlockedModal(true);
      return;
    }

    if (result?.error) {
      // Revert
      setUsers((prev) =>
        prev?.map((u) => (u?.id === user?.id ? { ...u, role: user?.role } : u))
      );
      showToast(t('userManagement.errors.updateFailed'), 'error');
      return;
    }

    showToast(t('userManagement.toasts.roleUpdated'));
  };

  // ── OnSite+ toggle flow ──────────────────────────────────────────────────────
  const handleOnsitePlusToggle = (user) => {
    const newVal = !user?.onsite_plus_enabled;
    setPendingAction({
      type: 'onsite',
      user,
      newVal,
      title: t('userManagement.modals.onsitePlus.title'),
      message: (newVal ? t('userManagement.modals.onsitePlus.enableMessage') : t('userManagement.modals.onsitePlus.disableMessage'))?.replace('{name}', user?.full_name || user?.email),
    });
  };

  const confirmOnsitePlusToggle = async () => {
    const { user, newVal } = pendingAction;
    setPendingAction(null);

    // Optimistic
    setUsers((prev) =>
      prev?.map((u) => (u?.id === user?.id ? { ...u, onsite_plus_enabled: newVal } : u))
    );

    let result = await setOnsitePlusEnabled(user?.id, newVal);

    if (result?.error) {
      setUsers((prev) =>
        prev?.map((u) => (u?.id === user?.id ? { ...u, onsite_plus_enabled: user?.onsite_plus_enabled } : u))
      );
      showToast(t('userManagement.errors.updateFailed'), 'error');
      return;
    }

    showToast(t('userManagement.toasts.onsiteUpdated'));
  };

  // ── View All Projects toggle flow ────────────────────────────────────────────
  const handleViewAllProjectsToggle = (user) => {
    const newVal = !user?.can_view_all_projects;
    setPendingAction({
      type: 'viewAllProjects',
      user,
      newVal,
      title: t('userManagement.modals.viewAllProjects.title'),
      message: (newVal
        ? t('userManagement.modals.viewAllProjects.enableMessage')
        : t('userManagement.modals.viewAllProjects.disableMessage'))
        ?.replace('{name}', user?.full_name || user?.email),
    });
  };

  const confirmViewAllProjectsToggle = async () => {
    const { user, newVal } = pendingAction;
    setPendingAction(null);

    // Optimistic
    setUsers((prev) =>
      prev?.map((u) => (u?.id === user?.id ? { ...u, can_view_all_projects: newVal } : u))
    );

    let result = await setCanViewAllProjects(user?.id, newVal);

    if (result?.error) {
      setUsers((prev) =>
        prev?.map((u) => (u?.id === user?.id ? { ...u, can_view_all_projects: user?.can_view_all_projects } : u))
      );
      showToast(t('userManagement.errors.updateFailed'), 'error');
      return;
    }

    showToast(t('userManagement.toasts.viewAllProjectsUpdated'));
  };

  // ── Active toggle flow ───────────────────────────────────────────────────────
  const handleToggleActive = (user) => {
    const newVal = !user?.is_active;
    setPendingAction({
      type: 'active',
      user,
      newVal,
      title: newVal
        ? t('userManagement.modals.reactivate.title')
        : t('userManagement.modals.deactivate.title'),
      message: (newVal ? t('userManagement.modals.reactivate.message') : t('userManagement.modals.deactivate.message'))?.replace('{name}', user?.full_name || user?.email),
      warning: !newVal ? t('userManagement.modals.deactivate.warning') : null,
    });
  };

  const confirmToggleActive = async () => {
    const { user, newVal } = pendingAction;
    setPendingAction(null);

    // Optimistic
    setUsers((prev) =>
      prev?.map((u) => (u?.id === user?.id ? { ...u, is_active: newVal } : u))
    );

    let result = await setUserActive(user?.id, newVal, user?.role);

    if (result?.blocked) {
      setUsers((prev) =>
        prev?.map((u) => (u?.id === user?.id ? { ...u, is_active: user?.is_active } : u))
      );
      setBlockedReason('last_admin');
      setBlockedModal(true);
      return;
    }

    if (result?.error) {
      setUsers((prev) =>
        prev?.map((u) => (u?.id === user?.id ? { ...u, is_active: user?.is_active } : u))
      );
      showToast(t('userManagement.errors.updateFailed'), 'error');
      return;
    }

    showToast(newVal ? t('userManagement.toasts.reactivated') : t('userManagement.toasts.deactivated'));
  };

  // ── Confirm dispatcher ───────────────────────────────────────────────────────
  const handleConfirm = () => {
    if (!pendingAction) return;
    if (pendingAction?.type === 'role') confirmRoleChange();
    else if (pendingAction?.type === 'onsite') confirmOnsitePlusToggle();
    else if (pendingAction?.type === 'viewAllProjects') confirmViewAllProjectsToggle();
    else if (pendingAction?.type === 'active') confirmToggleActive();
  };

  // ── Initials helper ──────────────────────────────────────────────────────────
  const getInitials = (user) => {
    if (user?.full_name) {
      const parts = user?.full_name?.trim()?.split(' ');
      if (parts?.length >= 2) return `${parts?.[0]?.[0]}${parts?.[1]?.[0]}`?.toUpperCase();
      return parts?.[0]?.[0]?.toUpperCase();
    }
    return user?.email?.[0]?.toUpperCase() || 'U';
  };

  if (!userProfile || !isAdmin(userProfile)) return null;

  return (
    <div className="pt-0 pb-0 px-0">
      {/* Page Header — matches Project List inline pattern */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <Icon name="Users" size={20} className="text-primary" />
          <h1 className="text-lg font-semibold text-[#2C3E50]">
            {t('userManagement.pageTitle')}
          </h1>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-xs font-normal text-[#6B7280]">
            {t('userManagement.pageSubtitle')}
          </p>
          {isAdmin(userProfile) && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-[#FF5A1A] rounded-lg transition-colors shadow-sm"
            >
              <Icon name="UserPlus" size={13} />
              {t('userManagement.invite.button')}
            </button>
          )}
        </div>
      </div>

      {/* Table — row-card pattern matching Project List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Icon name="Loader2" size={24} className="animate-spin text-primary" />
          <span className="ml-2 text-sm text-[#6B7280]">{t('common.loading')}</span>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center py-16 text-red-500 text-sm gap-2">
          <Icon name="AlertCircle" size={18} />
          {error}
        </div>
      ) : (
        <div className="space-y-1.5">
          {/* Header Row */}
          <div className="grid items-center h-10 px-4" style={{ gridTemplateColumns: 'minmax(200px,1.3fr) 160px 150px 110px 120px 110px 60px', gap: '12px' }}>
            <div className="text-xs font-semibold text-foreground/80">
              {t('userManagement.table.user')}
            </div>
            <div className="text-xs font-semibold text-foreground/80">
              {t('userManagement.table.role')}
            </div>
            <div className="text-xs font-semibold text-foreground/80">
              {t('userManagement.table.trade')}
            </div>
            <div className="text-xs font-semibold text-foreground/80 text-center">
              {t('userManagement.table.onsitePlus')}
            </div>
            <div className="text-xs font-semibold text-foreground/80 text-center">
              {t('userManagement.table.viewAllProjects')}
            </div>
            <div className="text-xs font-semibold text-foreground/80">
              {t('userManagement.table.status')}
            </div>
            <div className="text-xs font-semibold text-foreground/80 text-center">
              {t('userManagement.table.actions')}
            </div>
          </div>

          {/* Data Rows */}
          {users?.map((user) => {
            const isSelf = user?.id === userProfile?.id;
            const isAdminRole = user?.role === 'admin';
            const isAccountantRole = user?.role === 'accountant';
            const viewAllDisabled = isSelf || isAdminRole || isAccountantRole;
            const viewAllTooltip = isSelf
              ? t('userManagement.tooltips.viewAllProjectsSelf')
              : isAdminRole
              ? t('userManagement.tooltips.viewAllProjectsAdmin')
              : isAccountantRole
              ? t('userManagement.tooltips.viewAllProjectsAccountant')
              : null;

            return (
              <div
                key={user?.id}
                className="grid items-center min-h-[68px] bg-white border border-gray-200 rounded-lg shadow-[0_1px_3px_0_rgba(0,0,0,0.04),0_1px_2px_0_rgba(0,0,0,0.02)] hover:border-primary transition-all duration-200 px-4"
                style={{ gridTemplateColumns: 'minmax(200px,1.3fr) 160px 150px 110px 120px 110px 60px', gap: '12px' }}
              >
                {/* Avatar + Name + Email */}
                <div className="flex items-center gap-3 min-w-0">
                  <UserAvatar user={user} size={30} />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-[#2C3E50] leading-tight truncate">
                      {user?.full_name || '—'}
                    </p>
                    <p className="text-xs text-[#6B7280] truncate">{user?.email}</p>
                  </div>
                </div>

                {/* La fonction, et rien d'autre. L'acces n'est plus choisi ici :
                    un trigger tient permission_template_id aligne sur le role.
                    Ce qu'une fonction ouvre se regle dans Parametres. */}
                <div className="flex items-center gap-1.5 min-w-0">
                  <RoleDropdown
                    user={user}
                    currentUserId={userProfile?.id}
                    onRoleChange={handleRoleChange}
                    t={t}
                  />
                  <span className="relative group flex-shrink-0">
                    <Icon name="Info" size={11} className="text-[#C7CDD4]" />
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block z-50 pointer-events-none">
                      <span className="block bg-[#2C3E50] text-white text-[10px] rounded-md px-2 py-1 w-52 shadow-lg">
                        {t('userManagement.table.roleHelp')}
                      </span>
                    </span>
                  </span>
                </div>

                {/* Metier — facultatif, alimente par organization_trades */}
                <div className="flex items-center min-w-0">
                  <TradeDropdown
                    user={user}
                    trades={trades}
                    onTradeChange={handleTradeChange}
                    t={t}
                  />
                </div>

                {/* OnSite+ Toggle */}
                <div className="flex items-center justify-center">
                  <button
                    onClick={() => handleOnsitePlusToggle(user)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                      user?.onsite_plus_enabled ? 'bg-primary' : 'bg-gray-200'
                    }`}
                    aria-label={t('userManagement.table.onsitePlus')}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                        user?.onsite_plus_enabled ? 'translate-x-4' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* View All Projects Toggle */}
                <div className="flex items-center justify-center">
                  <div className="relative group inline-flex items-center justify-center">
                    <button
                      onClick={() => !viewAllDisabled && handleViewAllProjectsToggle(user)}
                      disabled={viewAllDisabled}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                        viewAllDisabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'
                      } ${
                        user?.can_view_all_projects ? 'bg-primary' : 'bg-gray-200'
                      }`}
                      aria-label={t('userManagement.table.viewAllProjects')}
                    >
                      <span
                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                          user?.can_view_all_projects ? 'translate-x-4' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    {viewAllDisabled && viewAllTooltip && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50 pointer-events-none">
                        <div className="bg-[#2C3E50] text-white text-xs rounded-md px-2 py-1 whitespace-nowrap shadow-lg">
                          {viewAllTooltip}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Status Pill */}
                <div>
                  <Badge variant={statusVariant('user', !!user?.is_active)}>
                    {user?.is_active
                      ? t('userManagement.status.active')
                      : t('userManagement.status.inactive')}
                  </Badge>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-center">
                  <ActionsMenu
                    user={user}
                    currentUserId={userProfile?.id}
                    onToggleActive={handleToggleActive}
                    t={t}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Organization Info Section (admin-only) */}
      <OrgInfoSection t={t} />

      {/* Confirmation Modal */}
      <ConfirmDialog
        isOpen={!!pendingAction}
        title={pendingAction?.title}
        message={pendingAction?.message}
        warning={pendingAction?.warning}
        onConfirm={handleConfirm}
        onCancel={() => setPendingAction(null)}
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
      />
      {/* Blocked Modal (last-admin) */}
      <ConfirmDialog
        isOpen={blockedModal}
        title={t('userManagement.modals.blocked.title')}
        message={
          blockedReason === 'last_admin_users_page'
            ? t('userManagement.modals.blocked.lastAdminUsersPage')
            : t('userManagement.modals.blocked.message')
        }
        onConfirm={() => setBlockedModal(false)}
        onCancel={() => setBlockedModal(false)}
        confirmLabel={t('common.close')}
        confirmOnly
      />
      {/* Invite User Modal */}
      {showInviteModal && (
        <InviteUserModal
          trades={trades}
          onClose={() => setShowInviteModal(false)}
          onSuccess={(mode) => {
            setShowInviteModal(false);
            if (mode === 'password') {
              showToast(t('userManagement.toasts.invitedPassword'));
            } else {
              showToast(t('userManagement.toasts.invitedEmail'));
            }
            fetchUsers();
          }}
          t={t}
        />
      )}
      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-[10001] flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all ${
            toast?.type === 'error' ? 'bg-red-600 text-white' : 'bg-[#2C3E50] text-white'
          }`}
        >
          <Icon name={toast?.type === 'error' ? 'AlertCircle' : 'CheckCircle'} size={16} />
          {toast?.message}
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;
